import { create } from 'zustand';
import { apiFetch } from '../lib/api';

/**
 * Módulos ativos do tenant logado (REQ-009 Adendo / AB#68 + AB#69).
 *
 * Populado uma vez no boot via `ModulosBoot` chamando `/minha-conta/modulos`.
 * O backend já aplica a regra do trial (AB#69): tenant em TRIAL recebe
 * TODOS os módulos do catálogo; tenant ATIVO recebe só os com TenantModulo
 * ativo; em TRIAL_EXPIRADO/SUSPENSO/CANCELADO o array vem vazio.
 *
 * Consumidores (ex.: menu lateral com cadeado da AB#67) chamam
 * `temModulo('mod_03')` para saber se devem mostrar/habilitar a tela.
 */

export interface Modulo {
  id: number;
  codigo: string;
  nome: string;
}

interface ModulosState {
  modulos: Modulo[];
  carregado: boolean;
  carregando: boolean;
  recarregar: () => Promise<void>;
  limpar: () => void;
  temModulo: (codigo: string) => boolean;
}

export const useModulosStore = create<ModulosState>((set, get) => ({
  modulos: [],
  carregado: false,
  carregando: false,
  /**
   * [fix refresh-sistêmico] Recarregamento SILENCIOSO em refetches.
   *
   * Antes: cada chamada fazia `set({ carregando: true })` no início,
   * disparando re-render de tudo que consome o store (menu/gating,
   * dropdowns, telas) → flash perceptível em QA (que é mais lento).
   *
   * Agora:
   *  - 1ª chamada (carregado=false): seta `carregando: true` pra UI
   *    distinguir estado inicial — mantém comportamento atual no boot.
   *  - Refetches (carregado=true): NÃO mexe em `carregando` nem
   *    esvazia `modulos`. A lista existente fica visível até a
   *    resposta nova chegar; quando chega, `set({ modulos })`
   *    substitui sem flash.
   *
   * Erro/HTTP-falha: mantém estado atual (não esvazia). O usuário
   * continua vendo os módulos da última leitura boa.
   */
  recarregar: async () => {
    const primeiraVez = !get().carregado;
    if (primeiraVez) set({ carregando: true });
    try {
      const res = await apiFetch('/minha-conta/modulos');
      if (!res.ok) {
        // 401 (sem sessão) é o caso esperado quando ainda não tem token —
        // ModulosBoot só dispara quando há `usuario` no auth.store.
        if (primeiraVez) set({ carregando: false });
        return;
      }
      const lista = (await res.json()) as Modulo[];
      set({ modulos: lista, carregado: true, carregando: false });
    } catch {
      if (primeiraVez) set({ carregando: false });
    }
  },
  limpar: () => set({ modulos: [], carregado: false, carregando: false }),
  temModulo: (codigo: string) => get().modulos.some((m) => m.codigo === codigo),
}));
